import { CurrentUser } from '@appTypes/CurrentUser';
import { DealerState } from '@appTypes/DealerState';
import { PasswordValidationResponse } from '@appTypes/header/PasswordValidationResponse';
import { UserProfile, UserProfileFormResource } from '@appTypes/header/UserProfile';
import { useUserDealer } from '@hooks/UseUserDealer';
import { UsersService } from '@services/users/UsersService';
import { ErrorToast, SuccessToast } from '@services/utils/ToastUtils';
import { AppState } from '@store/AppState';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

export const useUserProfileForm = (
  formData: UserProfileFormResource,
  showChangePassword: boolean,
  onClose: () => void,
  setIsSaving: Dispatch<SetStateAction<boolean>>,
  setSubmitted: Dispatch<SetStateAction<boolean>>,
  setShowChangePassword: Dispatch<SetStateAction<boolean>>,
  isOtpSubmittedAndVerified?: boolean,
  displayPhoneNumber?: string,
): UseUserProfileFormType => {
  const realtimeServiceArrivalFlag: number = 1;
  const serviceArrivalFlag: number = 2;
  const regExpPassword = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!?$@&#_%.-]).{8,}/;

  const [isPasswordChanged, setIsPasswordChanged] = useState<boolean>(false);
  const [currentPasswordIncorrect, setCurrentPasswordIncorrect] = useState<boolean>(false);
  const [invalidPassword, setInvalidPassword] = useState<boolean>(false);
  const [passwordValidationResponse, setPasswordValidationResponse] =
    useState<PasswordValidationResponse>(null);

  const currentPasswordVerified = useRef<boolean>(false);

  const { featureToggle } = useSelector<AppState, DealerState>((state) => state.dealer);
  const mobileNotifications = featureToggle?.mobileNotifications;

  const { isIncentiveAdvantageType, isEssentialType } = useUserDealer();

  const {
    firstName,
    lastName,
    userName,
    shadowUserFirstName,
    shadowUserLastName,
    shadowUserName,
    isPrimaryUser,
  } = useSelector<AppState, CurrentUser>((state) => state.currentUser);
  const displayFirstName = shadowUserFirstName ?? firstName;
  const displayLastName = shadowUserLastName ?? lastName;
  const displayUserName = shadowUserName
    ? shadowUserName.replace('@', '%20') + '/' + userName
    : userName;
  const showChangePasswordToggle = () => {
    setCurrentPasswordIncorrect(false);
    setShowChangePassword(!showChangePassword);
    setIsPasswordChanged(!isPasswordChanged);
  };

  const hasAnyError = (): boolean => {
    if (!showChangePassword) {
      return false;
    }

    if (!passwordValidationResponse) {
      return false;
    }
    return (
      passwordValidationResponse.invalidSpecialCharacter ||
      !passwordValidationResponse.minLength ||
      !passwordValidationResponse.number ||
      !passwordValidationResponse.lowerCase ||
      !passwordValidationResponse.upperCase ||
      !passwordValidationResponse.specialCharacter
    );
  };

  const verifyCurrentPassword = async (): Promise<void> => {
    const password: string = formData.currentPassword;
    await UsersService.getVerifyCurrentUserPassword(displayUserName, password)
      .then(() => {
        currentPasswordVerified.current = true;
      })
      .catch(() => {
        currentPasswordVerified.current = false;
      });
  };

  const onSaveClick = (): void => {
    let passwordDoesntMatch = false;
    const passwordField =
      isPrimaryUser || isIncentiveAdvantageType ? formData.password : formData.newPasswordDealer;
    const confirmPasswordField =
      isPrimaryUser || isIncentiveAdvantageType
        ? formData.confirmPassword
        : formData.confirmPasswordDealer;

    const invalidPassword = passwordField && !regExpPassword.test(passwordField);
    setInvalidPassword(invalidPassword);
    const userProfileData: UserProfile = {
      emailPreferences:
        (formData.realtimeServiceArrivalEmail ? realtimeServiceArrivalFlag : 0) |
        (formData.serviceArrivalEmail ? serviceArrivalFlag : 0),
      newPassword: passwordField,
      homepagePreference: parseInt(formData.homePagePreference),
    };

    if (!isPrimaryUser) {
      if (displayPhoneNumber === '') {
        formData.phoneNumber = null;
      }

      userProfileData.phoneNumber = formData.phoneNumber;

      if (mobileNotifications) {
        userProfileData.isOptedOutSms = formData.isOptedOutSms;

        userProfileData.notifications = formData.notifications;
      }
    }

    const passwordRequiredFailed = !passwordField;
    const currentPasswordRequiredFailed = !formData.currentPassword;

    if (passwordField !== confirmPasswordField) {
      userProfileData.newPassword = '';
      passwordDoesntMatch = true;
    }

    if (!isPrimaryUser && !showChangePassword && !isIncentiveAdvantageType) {
      userProfileData.newPassword = '';
    }

    if (hasAnyError()) {
      return;
    }

    if (!isPrimaryUser && !isIncentiveAdvantageType) {
      if (!isPasswordChanged) {
        setIsSaving(true);
        updateUserProfile(userProfileData, passwordDoesntMatch);
      }
      if (
        isPasswordChanged &&
        showChangePassword &&
        !passwordRequiredFailed &&
        !invalidPassword &&
        !passwordDoesntMatch
      ) {
        if (currentPasswordRequiredFailed) {
          return;
        }
        setIsSaving(true);

        verifyCurrentPassword().then(() => {
          if (!currentPasswordVerified.current) {
            userProfileData.newPassword = '';
          }
          updateUserProfile(userProfileData, passwordDoesntMatch);
        });
      }
    } else if (!passwordRequiredFailed && !invalidPassword && !passwordDoesntMatch) {
      setIsSaving(true);
      updateUserProfile(userProfileData, passwordDoesntMatch);
    }
  };

  const updateUserProfile = (userProfileData: UserProfile, passwordDoesntMatch: boolean) => {
    UsersService.putUserProfile(userProfileData)
      .then((res) => {
        if (!isPrimaryUser) {
          if (res.status === 200 && !isPasswordChanged) {
            setTimeout(() => {
              SuccessToast('Updated profile.');
            }, 500);
            onClose();
          }
          if (
            res.status === 200 &&
            !passwordDoesntMatch &&
            currentPasswordVerified.current &&
            isPasswordChanged
          ) {
            setTimeout(() => {
              SuccessToast('Updated profile.');
            }, 500);
            onClose();
          } else if (res.status === 200 && !passwordDoesntMatch && isPasswordChanged) {
            setCurrentPasswordIncorrect(true);
            ErrorToast('Error: password not saved successfully');
          } else if (res.status === 200 && passwordDoesntMatch && isPasswordChanged) {
            ErrorToast('Error: password not saved successfully');
          }
        } else {
          if (res.status === 200 && !passwordDoesntMatch) {
            setTimeout(() => {
              SuccessToast('Updated profile.');
            }, 500);
            onClose();
          } else {
            ErrorToast('Error updating profile.');
          }
        }
      })
      .catch((err) => {
        ErrorToast(`'An error has occurred: ${err}'`);
      })
      .finally(() => {
        setIsSaving(false);
        setSubmitted(false);
      });
  };

  return {
    displayFirstName,
    displayLastName,
    displayUserName,
    isEssentialType,
    isIncentiveAdvantageType,
    isPrimaryUser,
    invalidPassword,
    passwordValidationResponse,
    currentPasswordIncorrect,
    showChangePasswordToggle,
    setPasswordValidationResponse,
    setInvalidPassword,
    onSaveClick,

    setCurrentPasswordIncorrect,
    setIsPasswordChanged,
  };
};

type UseUserProfileFormType = {
  displayFirstName: string;
  displayLastName: string;
  displayUserName: string;
  isPrimaryUser: boolean;
  isIncentiveAdvantageType: boolean;
  isEssentialType: boolean;
  invalidPassword: boolean;
  passwordValidationResponse: PasswordValidationResponse;
  currentPasswordIncorrect: boolean;
  showChangePasswordToggle: () => void;
  setPasswordValidationResponse: Dispatch<SetStateAction<PasswordValidationResponse>>;
  setInvalidPassword: Dispatch<SetStateAction<boolean>>;
  onSaveClick: () => void;

  setCurrentPasswordIncorrect: Dispatch<SetStateAction<boolean>>;
  setIsPasswordChanged: Dispatch<SetStateAction<boolean>>;
};
